import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { User, JWTPayload, AuthTokens } from "@clawplete/shared/types";

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  _hasHydrated: boolean;
  setAuth: (user: User, tokens: AuthTokens) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<User>) => void;
  syncUser: (api: any) => Promise<void>;
}

// Track hydration state outside of store to prevent loops
let hasHydrated = false;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      _hasHydrated: false,
      setAuth: (user, tokens) => {
        localStorage.setItem("access_token", tokens.access_token);
        localStorage.setItem("refresh_token", tokens.refresh_token);
        set({ user, tokens, isAuthenticated: true });
      },
      // Fetch fresh user data from server to ensure localStorage is in sync
      syncUser: async (api: any) => {
        try {
          const response = await api.get("/auth/me");
          if (response.data.success) {
            const { user: freshUser } = response.data.data;
            set((state) => ({
              user: { ...state.user, ...freshUser }, // Merge fresh data over existing state
              isAuthenticated: true,
            }));
          }
        } catch (error: any) {
          // Silently handle auth errors - axios interceptor will handle token refresh/redirect
          if (error.response?.status !== 401) {
            console.error("Failed to sync user data:", error);
          }
        }
      },
      clearAuth: () => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        // Clear zustand persisted storage too
        localStorage.removeItem("auth-storage");
        set({ user: null, tokens: null, isAuthenticated: false });
      },
      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ user: state.user, tokens: state.tokens }),
      onRehydrateStorage: () => (state) => {
        // Derive isAuthenticated from hydrated state and mark as hydrated (only once)
        if (state && !hasHydrated) {
          hasHydrated = true;
          state.isAuthenticated = !!(state.user && state.tokens);
          state._hasHydrated = true;

          // Restore tokens to localStorage for axios interceptor
          if (state.tokens) {
            localStorage.setItem("access_token", state.tokens.access_token);
            localStorage.setItem("refresh_token", state.tokens.refresh_token);
          }
        }
      },
    }
  )
);
